import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';

import {
    Checkbox, Flex, Image, Label, LabeledControl, Text, TextButton, Trigger,
} from '@zillow/constellation';
import { Col, Row } from '@zillow/zrs-comet';

import RentGuaranteeActions from 'app/shared/actions/RentGuaranteeActions';

import WizardBodyTemplate from 'app/shared/components/WizardBodyTemplate';
import WizardActionFooter from 'app/shared/components/WizardActionFooter';
import ToolExplanationModal from 'app/shared/components/Modal/ToolExplanationModal';
import RentGuaranteeApplications from 'app/shared/images/RentGuaranteeApplications.svg';
import RentGuaranteeLeases from 'app/shared/images/RentGuaranteeLeases.svg';
import RentGuaranteePayments from 'app/shared/images/RentGuaranteePayments.svg';

const propTypes = {
    onGoToPrevStep: PropTypes.func.isRequired,
    onGoToNextStep: PropTypes.func.isRequired,
};
const defaultProps = {};

const OnlineTools = ({ onGoToPrevStep, onGoToNextStep }) => {
    const dispatch = useDispatch();
    const isOnlineToolsEnabled = useSelector(state => state.rentGuarantee.isOnlineToolsEnabled);

    const toolExplanationModal = (props, close) => (
        <ToolExplanationModal {...props} close={close} />
    );

    return (
        <WizardBodyTemplate
            title="Rent guarantee's online tools"
            subtitle="Get seamless property management with Rent Guarantee’s convenient application, lease, and payment
            tools for finding the right tenant and managing your rental."
        >
            <Row marginBottom="lg">
                <Col xs={4}>
                    <Flex display="flex" flexDirection="row" justifyContent="center">
                        <Image
                            alt="Applications"
                            src={RentGuaranteeApplications}
                            marginBottom="md"
                        />
                    </Flex>
                    <Text fontType="bodyHeading">Applications. </Text>
                    <Text>
                        Screening tenants has never been easier, thanks to automatic background and credit checks.
                        We pull the so you can quickly confirm if applicants are eligible for Rent Guarantee.
                    </Text>
                </Col>
                <Col xs={4}>
                    <Flex display="flex" flexDirection="row" justifyContent="center">
                        <Image
                            alt="Lesases"
                            src={RentGuaranteeLeases}
                            marginBottom="md"
                        />
                    </Flex>
                    <Text fontType="bodyHeading">Leases. </Text>
                    <Text>
                        Upload your lease to Zillow, or create a new one using our tool. With your lease terms and
                        tenant info on hand, your claim can be received and processed quickly.
                    </Text>
                </Col>
                <Col xs={4}>
                    <Flex display="flex" flexDirection="row" justifyContent="center">
                        <Image
                            alt="Payments"
                            src={RentGuaranteePayments}
                            marginBottom="md"
                        />
                    </Flex>
                    <Text fontType="bodyHeading">Payments. </Text>
                    <Text>
                        With Zillow online payments, your tenant can pay rent directly into your bank account, and
                        we’ll have a record of any missed payments, which can help speed up your claim processing.
                    </Text>
                </Col>
            </Row>
            <Trigger triggered={toolExplanationModal}>
                <TextButton marginBottom="sm">Why Rent Guarantee requires using these tools </TextButton>
            </Trigger>
            <Flex display="flex" justifyContent="center" marginBottom="lg">
                <LabeledControl
                    label={<Label>I understand that these Zillow tools are required for Rent Guarantee</Label>}
                    control={(
                        <Checkbox
                            checked={isOnlineToolsEnabled}
                            onClick={() => dispatch(RentGuaranteeActions.setIsOnlineToolsEnabled(
                                !isOnlineToolsEnabled
                            ))}
                        />
                    )}
                />
            </Flex>
            <WizardActionFooter
                onBackClick={onGoToPrevStep}
                onNextClick={onGoToNextStep}
                isNextDisabled={!isOnlineToolsEnabled}
            />
        </WizardBodyTemplate>
    );
};

OnlineTools.propTypes = propTypes;
OnlineTools.defaultProps = defaultProps;

export default OnlineTools;
